import React, { useEffect, useMemo, useRef, useState } from "react";
import { Col, Form, Input, Row } from "antd";
import PhoneInput from "antd-phone-input";
import { set } from "lodash";

const PhoneNumberInput = ({
  locationType,
  formItemProps: _formItemProps,
  showExtensionInput = true,
  borderRadius = 4,
  // onChange,
  disabled = false,
  nextInputRef = undefined,
  ...params
}) => {
  const [extensionValue, setExtensionValue] = useState("");
  const extensionInputRef = useRef();

  const formItemProps = useMemo(() => {
    const newParams = { ..._formItemProps };
    let { rules } = newParams;

    if (!rules) {
      rules = [];
    }

    const validatorFn = async (_, { valid }) => {
      const isRequired = rules.some(rule => rule.required);

      if (!isRequired) {
        return Promise.resolve();
      }

      // pass true to enable strict validation, otherwise it will allow invalid phone numbers like +1 (111) 222 3333
      if (valid()) {
        return Promise.resolve();
      }

      const label = newParams?.label ? `Please enter a ${newParams?.label}` : "Invalid phone number";
      return Promise.reject(label);
    };

    rules.push({ validator: validatorFn });
    newParams.rules = rules;

    return newParams;
  }, [_formItemProps]);

  const phoneInputName = useMemo(() => {
    const { name } = formItemProps || {};

    if (locationType) {
      return [locationType, name];
    }

    return name;
  }, [locationType, formItemProps]);

  // use as key to force re-render when the value changes since we're updating the value through the form ref
  const currentValue = Form.useWatch(phoneInputName, formItemProps?.formRef);

  const extensionInputName = useMemo(() => {
    const defaultName = "phoneExtension";

    if (!formItemProps) {
      return defaultName;
    }

    const { name, extensionName } = formItemProps || {};

    if (!name && !extensionName) {
      return defaultName;
    }

    const extensionNameNotNested = extensionName || `${name}Extension`;

    // If nesting level provided in form (e.g. "Pickup", "Dropoff", etc)
    if (locationType) {
      return [locationType, extensionNameNotNested];
    }

    return extensionNameNotNested;
  }, [formItemProps, locationType]);

  // Add default country code of 1 for pasted phone numbers
  useEffect(() => {
    const handlePaste = e => {
      const targetClassName = Array.isArray(phoneInputName) ? phoneInputName.join(",") : phoneInputName;
      const isTargetInput = e.target.parentNode.parentNode.classList.contains(`${targetClassName}-phone-input`);
      if (!isTargetInput) return;

      const pastedText = e.clipboardData.getData("text");
      const pastedTextCleaned = pastedText
        .trim()
        .replaceAll("(", "")
        .replaceAll(")", "")
        .replaceAll("+", "")
        .replaceAll(".", "")
        .replaceAll("-", "")
        .replaceAll(" ", "");

      let updatedInputValue;

      if (pastedTextCleaned.startsWith("1")) {
        updatedInputValue = `1 ${pastedTextCleaned.slice(1)}`;
      } else {
        updatedInputValue = `1 ${pastedTextCleaned}`;
      }

      const newValues = {};

      set(newValues, phoneInputName, updatedInputValue);

      formItemProps?.formRef?.current?.setFieldsValue(newValues);
      e.preventDefault();
    };

    window.addEventListener("paste", handlePaste);

    return () => {
      window.removeEventListener("paste", handlePaste);
    };
  }, [phoneInputName, formItemProps]);

  const handleKeyDown = e => {
    if (!nextInputRef) return;

    if (e.key === "Tab") {
      e.preventDefault();
      nextInputRef.current.focus();
    }
  };

  return (
    <Row wrap={false} style={{ width: "100%" }}>
      <Col span={showExtensionInput ? 19 : 24}>
        <Form.Item
          style={{
            flex: 1,
            ...formItemProps?.style,
          }}
          name={phoneInputName}
          label={
            <label htmlFor={phoneInputName} style={{ maxHeight: "20px" }}>
              {formItemProps?.label}
            </label>
          }
          rules={formItemProps?.rules}
          validateTrigger={formItemProps?.validateTrigger}
        >
          <PhoneInput
            {...params}
            preferredCountries={["us", "ca", "mx"]}
            country="us"
            disabled={disabled}
            className={`${showExtensionInput ? "tel-input-with-extension" : ""} ${phoneInputName}-phone-input`}
            style={{
              borderRadius,
              marginTop: "1px",
            }}
            autoComplete="tel"
            type="tel"
            key={currentValue}
          />
        </Form.Item>
      </Col>

      {showExtensionInput && (
        <Col span={5}>
          <Form.Item
            label={
              <label htmlFor={extensionInputName} style={{ maxHeight: "20px" }}>
                {formItemProps?.extensionLabel ?? "Extension"}
              </label>
            }
            name={extensionInputName}
            style={{ flex: 1 }}
          >
            <Input
              disabled={disabled}
              ref={extensionInputRef}
              style={{
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                borderLeft: "none",
                height: "32px",
              }}
              value={extensionValue}
              onChange={e => {
                const digitsOnly = e.target.value.replace(/\D/g, "");
                setExtensionValue(digitsOnly);
                const newValues = {};
                set(newValues, extensionInputName, digitsOnly);
                formItemProps?.formRef?.current?.setFieldsValue(newValues);
              }}
              autoComplete="nope" // Set to ensure chrome autofill ignores this input
              type="text" // Set to ensure chrome autofill does not mistake for intended phone input
              id="extensionInput" // Cannot have "phone" in it or it will break chrome autofill
              onKeyDown={handleKeyDown}
            />
          </Form.Item>
        </Col>
      )}
    </Row>
  );
};

PhoneNumberInput.defaultProps = {
  showExtensionInput: true,
  borderRadius: 8,
  disabled: false,
  nextInputRef: undefined,
  locationType: undefined,
  formItemProps: undefined,
  onChange: undefined,
};

export default PhoneNumberInput;
